import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Save as SaveIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import axios from 'axios';
import { 
  AppVersion, 
  getAppVersions, 
  createAppVersion, 
  updateAppVersion, 
  deleteAppVersion 
} from '../../services/appVersionService';

const initialVersionState: Omit<AppVersion, 'id' | 'created_at' | 'updated_at'> = {
  platform: 'android',
  version: '',
  mandatory: true,
  enabled: true
};

const VersionesAppPage: React.FC = () => {
  const [versions, setVersions] = useState<AppVersion[]>([]);
  const [currentVersion, setCurrentVersion] = useState<AppVersion | Omit<AppVersion, 'id' | 'created_at' | 'updated_at'>>(initialVersionState);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [saveStatus, setSaveStatus] = useState<{success: boolean; message: string} | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

  // Obtener las versiones al cargar el componente
  useEffect(() => {
    let isMounted = true;
    
    const loadVersions = async () => {
      try {
        setIsLoading(true);
        const data = await getAppVersions();
        if (isMounted) {
          setVersions(data);
        }
      } catch (error) {
        console.error('Error al cargar versiones:', error);
        if (isMounted) {
          setSaveStatus({
            success: false,
            message: 'Error al cargar las versiones de la aplicación. Por favor, intente nuevamente.'
          });
          setSnackbarOpen(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadVersions();

    return () => {
      isMounted = false;
    };
  }, []);

  const fetchVersions = async () => {
    try {
      setIsLoading(true);
      const data = await getAppVersions();
      setVersions(data);
    } catch (error) {
      console.error('Error al cargar versiones:', error);
      setSaveStatus({
        success: false,
        message: 'Error al cargar las versiones de la aplicación. Por favor, intente nuevamente.'
      });
      setSnackbarOpen(true);
      throw error; // Relanzar el error para que pueda ser manejado por el llamador si es necesario
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (version?: AppVersion) => {
    if (version) {
      setCurrentVersion(version);
      setIsEditing(true);
    } else {
      setCurrentVersion(initialVersionState);
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentVersion(initialVersionState);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setCurrentVersion({
      ...currentVersion,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setCurrentVersion({
      ...currentVersion,
      [name]: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentVersion.version.trim()) {
      setSaveStatus({
        success: false,
        message: 'Por favor ingrese un número de versión válido'
      });
      setSnackbarOpen(true);
      return;
    }
    
    try {
      setIsLoading(true);
      
      if (isEditing && 'id' in currentVersion) {
        await updateAppVersion(currentVersion as AppVersion);
        setSaveStatus({
          success: true,
          message: 'Versión actualizada correctamente'
        });
      } else {
        await createAppVersion(currentVersion);
        setSaveStatus({
          success: true,
          message: 'Versión creada correctamente'
        });
      }
      
      setSnackbarOpen(true);
      await fetchVersions();
      handleCloseDialog();
    } catch (error) {
      console.error('Error al guardar la versión:', error);
      let errorMessage = `Error al ${isEditing ? 'actualizar' : 'crear'} la versión`;
      
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || errorMessage;
      }
      
      setSaveStatus({
        success: false,
        message: errorMessage
      });
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta versión?')) {
      try {
        setIsLoading(true);
        await deleteAppVersion(id);
        setSaveStatus({
          success: true,
          message: 'Versión eliminada correctamente'
        });
        setSnackbarOpen(true);
        fetchVersions();
      } catch (error) {
        console.error('Error al eliminar la versión:', error);
        setSaveStatus({
          success: false,
          message: 'Error al eliminar la versión'
        });
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Versiones de la Aplicación Clientes
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={isLoading}
        >
          Nueva Versión
        </Button>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {isLoading && versions.length === 0 ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : versions.length === 0 ? (
          <Box p={3} textAlign="center">
            <Typography variant="body1" color="textSecondary">
              No hay versiones registradas
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ width: '100%' }}>
            <Table
              sx={{
                width: '100%',
                '& thead .MuiTableCell-root': {
                  backgroundColor: '#f5f5f5',
                  fontWeight: 600,
                },
                '& tbody .MuiTableRow-root:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.02)'
                },
                '& .MuiTableCell-root': {
                  borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                  padding: '8px 16px',
                  verticalAlign: 'middle',
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Plataforma</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Versión</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Obligatoria</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.map((version) => (
                  <TableRow key={version.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {version.platform === 'android' ? 'Android' : 'iOS'}
                      </Box>
                    </TableCell>
                    <TableCell>{version.version}</TableCell>
                    <TableCell>
                      {version.mandatory ? (
                        <Typography color="primary" fontWeight={500}>Sí</Typography>
                      ) : (
                        <Typography color="textSecondary">No</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {version.enabled ? (
                          <>
                            <ActiveIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                            <Typography>Activa</Typography>
                          </>
                        ) : (
                          <>
                            <InactiveIcon color="disabled" fontSize="small" sx={{ mr: 1 }} />
                            <Typography color="textSecondary">Inactiva</Typography>
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Editar">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog(version)}
                            disabled={isLoading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => version.id && handleDelete(version.id)}
                            disabled={isLoading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Diálogo para crear/editar versión */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{ 
            bgcolor: 'primary.main', 
            color: 'primary.contrastText',
            py: 2,
            px: 3,
            '& .MuiTypography-root': {
              fontWeight: 600,
              fontSize: '1.25rem'
            }
          }}>
            {isEditing ? '✏️ Editar Versión' : '➕ Nueva Versión'}
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box display="flex" flexDirection="column" gap={3} mt={1}>
              <FormControl fullWidth>
                <InputLabel id="platform-label">Plataforma</InputLabel>
                <Select
                  labelId="platform-label"
                  name="platform"
                  value={currentVersion.platform}
                  onChange={handleSelectChange}
                  label="Plataforma"
                  required
                  disabled={isEditing}
                >
                  <MenuItem value="android">Android</MenuItem>
                  <MenuItem value="ios">iOS</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Versión"
                name="version"
                value={currentVersion.version}
                onChange={handleInputChange}
                required
                fullWidth
                placeholder="Ej: 1.0.0"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={currentVersion.mandatory}
                    onChange={handleInputChange}
                    name="mandatory"
                    color="primary"
                  />
                }
                label={
                  <Box display="flex" alignItems="center">
                    <Typography>Actualización obligatoria</Typography>
                    {currentVersion.mandatory && (
                      <Typography variant="caption" color="error" ml={1}>
                        (Los usuarios deberán actualizar para continuar usando la app)
                      </Typography>
                    )}
                  </Box>
                }
              />

              {isEditing && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentVersion.enabled}
                      onChange={(e) => 
                        setCurrentVersion({
                          ...currentVersion,
                          enabled: e.target.checked
                        })
                      }
                      name="enabled"
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center">
                      <Box 
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: currentVersion.enabled ? 'success.main' : 'error.main',
                          mr: 1
                        }} 
                      />
                      <Typography>
                        {currentVersion.enabled ? 'Activa' : 'Inactiva'}
                      </Typography>
                    </Box>
                  }
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button 
              onClick={handleCloseDialog} 
              color="inherit"
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={isLoading || !currentVersion.version}
            >
              {isEditing ? 'Actualizar' : 'Guardar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar para mostrar mensajes */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={saveStatus?.success ? 'success' : 'error'}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {saveStatus?.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default VersionesAppPage;
